/**
 * BestOddsDetailModal - "Odds intelligence" secondary screen for a single
 * market anomaly. Shows event/market context plus the full odds ladder
 * (every bookmaker's price for that outcome), sorted best to worst.
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import { getMarginTypeName, getOutcomeName, getBookmakerName } from '../../surebets/utils/surebetUtils';
import NumberUtils from '../../utils/NumberUtils';
import './BestOddsDetailModal.css';

const BestOddsDetailModal = ({ anomaly, isOpen, onClose }) => {
  const { t, language } = useTranslation();

  const sortedLadder = useMemo(() => {
    if (!anomaly?.oddsLadder) return [];
    return [...anomaly.oddsLadder].sort((a, b) => (b.value || 0) - (a.value || 0));
  }, [anomaly]);

  if (!isOpen || !anomaly) {
    return null;
  }

  const marketLabel = getMarginTypeName(anomaly.marketKey, language);
  const outcomeLabel = getOutcomeName(anomaly.marketKey, anomaly.outcomeType, language);

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="best-odds-detail-modal modal-open"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="best-odds-detail-modal-title"
      data-testid="best-odds-detail-modal"
    >
      <div className="best-odds-detail-modal__backdrop" />

      <div className="best-odds-detail-modal__container">
        <div className="best-odds-detail-modal__header">
          <div className="best-odds-detail-modal__header-content">
            <h2 id="best-odds-detail-modal-title" className="best-odds-detail-modal__title">
              {t('bestOdds.modal.title', 'Детайли за пазарна аномалия')}
            </h2>
          </div>

          <button
            type="button"
            className="modal-close-button"
            onClick={onClose}
            aria-label={t('bestOdds.modal.close', 'Затвори')}
          >
            <span className="close-icon">×</span>
          </button>
        </div>

        <div className="best-odds-detail-modal__content">
          <div className="best-odds-detail">
            <section className="best-odds-detail__section">
              <h3 className="best-odds-detail__section-title">
                {t('bestOdds.modal.eventDetails', 'Детайли за събитието')}
              </h3>
              <div className="best-odds-detail__teams">
                <span>{anomaly.event.teamOne}</span>
                <span className="vs">vs</span>
                <span>{anomaly.event.teamTwo}</span>
              </div>
              <div className="best-odds-detail__meta">
                <span>{anomaly.event.date}</span>
                <span>{anomaly.event.time}</span>
                <span>{anomaly.event.championship}</span>
              </div>
            </section>

            <section className="best-odds-detail__section">
              <div className="best-odds-detail__stat-grid">
                <div className="best-odds-detail__stat">
                  <span className="best-odds-detail__stat-label">
                    {t('bestOdds.modal.marketType', 'Тип пазар')}
                  </span>
                  <span className="best-odds-detail__stat-value">
                    {marketLabel} &middot; {outcomeLabel}
                  </span>
                </div>
                <div className="best-odds-detail__stat">
                  <span className="best-odds-detail__stat-label">
                    {t('bestOdds.modal.bestOddsLabel', 'Най-добър коефициент')}
                  </span>
                  <span className="best-odds-detail__stat-value best-odds-detail__stat-value--highlight">
                    {NumberUtils.formatOdds(anomaly.bestOdds, { decimals: 2 })} ({getBookmakerName(anomaly.bestBookmaker)})
                  </span>
                </div>
                <div className="best-odds-detail__stat">
                  <span className="best-odds-detail__stat-label">
                    {t('bestOdds.modal.marketAvgLabel', 'Пазарна средна')}
                  </span>
                  <span className="best-odds-detail__stat-value">
                    {NumberUtils.formatOdds(anomaly.marketAvg, { decimals: 2 })}
                  </span>
                </div>
                <div className="best-odds-detail__stat">
                  <span className="best-odds-detail__stat-label">
                    {t('bestOdds.modal.spreadLabel', 'Отклонение')}
                  </span>
                  <span className="best-odds-detail__stat-value best-odds-detail__stat-value--positive">
                    +{NumberUtils.formatPercentage(anomaly.spread, { decimals: 1, showSymbol: true })}
                  </span>
                </div>
                <div className="best-odds-detail__stat">
                  <span className="best-odds-detail__stat-label">
                    {t('bestOdds.modal.bookmakerCountLabel', 'Брой букмейкъри')}
                  </span>
                  <span className="best-odds-detail__stat-value">{anomaly.bookmakerCount}</span>
                </div>
              </div>
            </section>

            <section className="best-odds-detail__section">
              <h3 className="best-odds-detail__section-title">
                {t('bestOdds.modal.oddsLadderTitle', 'Всички коефициенти')}
              </h3>
              <table className="best-odds-detail__ladder-table">
                <thead>
                  <tr>
                    <th>{t('bestOdds.modal.oddsLadderBookmaker', 'Букмейкър')}</th>
                    <th>{t('bestOdds.modal.oddsLadderOdds', 'Коефициент')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLadder.map((entry) => (
                    <tr
                      key={entry.bookmaker}
                      className={entry.bookmaker === anomaly.bestBookmaker ? 'best-odds-detail__ladder-row--best' : ''}
                    >
                      <td>{getBookmakerName(entry.bookmaker)}</td>
                      <td>{NumberUtils.formatOdds(entry.value, { decimals: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

BestOddsDetailModal.propTypes = {
  anomaly: PropTypes.shape({
    event: PropTypes.shape({
      teamOne: PropTypes.string,
      teamTwo: PropTypes.string,
      date: PropTypes.string,
      time: PropTypes.string,
      championship: PropTypes.string
    }),
    marketKey: PropTypes.string,
    outcomeType: PropTypes.string,
    bestBookmaker: PropTypes.string,
    bestOdds: PropTypes.number,
    marketAvg: PropTypes.number,
    spread: PropTypes.number,
    bookmakerCount: PropTypes.number,
    oddsLadder: PropTypes.arrayOf(PropTypes.shape({
      bookmaker: PropTypes.string,
      value: PropTypes.number
    }))
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default BestOddsDetailModal;
